/**
 * Financial section vocabulary and content types.
 *
 * Ported from the standalone Realiform codebase (Next.js + D1 + Drizzle) into
 * this SPA. The Drizzle table definitions are gone — nothing here is stored in
 * a database — but the *shape* of a case study is preserved exactly, including
 * the provenance fields. Those fields are the point: a modelled figure and a
 * reported figure look identical on a page unless something forces the
 * difference to be stated, and `sourceType` is that something.
 *
 * MONEY IS INTEGER CENTS, EVERYWHERE. Never floats. A "$1,247.50/mo revenue"
 * figure that drifts by a rounding error undermines the premise of the section.
 * `format.ts` is the only place cents become strings.
 */

/* -------------------------------------------------------------------------- */
/*                                 Vocabulary                                  */
/* -------------------------------------------------------------------------- */

export const CASE_STUDY_CATEGORIES = [
  'local-cashflow', // laundromats, route businesses, vending, self-storage
  'micro-saas', // small recurring-revenue software utilities
  'digital-asset', // PDFs, templates, notion kits, one-off tools
  'debt-arbitrage', // spread plays financed with structured debt
  'solo-structure', // entity setup, tax positioning, solo operator mechanics
  'service-business', // productised services, agencies, contractor work
  'diaspora-succession', // first-generation businesses changing hands within the community
] as const;
export type CaseStudyCategory = (typeof CASE_STUDY_CATEGORIES)[number];

export const EFFORT_LEVELS = ['passive', 'part-time', 'full-time'] as const;
export type EffortLevel = (typeof EFFORT_LEVELS)[number];

export const DIFFICULTY_LEVELS = ['starter', 'intermediate', 'advanced'] as const;
export type DifficultyLevel = (typeof DIFFICULTY_LEVELS)[number];

/**
 * Sourcing tier. Drives the badge on every case study.
 *
 * `/financial/methodology` explains what each one means and — more usefully —
 * what it cannot tell the reader.
 */
export const SOURCE_TYPES = [
  'operator-interview',
  'public-filing',
  'listing-data',
  'composite', // blended from several real businesses
  'illustrative', // modelled, not a specific real business
] as const;
export type SourceType = (typeof SOURCE_TYPES)[number];

export const PLAYBOOK_FORMATS = ['Checklist', 'Template', 'Blueprint', 'Spreadsheet'] as const;
export type PlaybookFormat = (typeof PLAYBOOK_FORMATS)[number];

export const CALCULATOR_KINDS = [
  'cash-on-cash',
  'business-valuation',
  'break-even',
  'solo-tax',
] as const;
export type CalculatorKind = (typeof CALCULATOR_KINDS)[number];

/* -------------------------------------------------------------------------- */
/*                                Presentation                                 */
/* -------------------------------------------------------------------------- */

/**
 * Display labels for the category vocabulary.
 *
 * Realiform derived these by title-casing the slug with a table of overrides.
 * An explicit map is shorter here and lets the Korean label sit beside the
 * English one, which the rest of this site does everywhere.
 */
export const CATEGORY_LABELS: Record<CaseStudyCategory, { label: string; labelKo: string }> = {
  'local-cashflow': { label: 'Local Cash Flow', labelKo: '지역 현금흐름' },
  'micro-saas': { label: 'Micro-SaaS', labelKo: '마이크로 SaaS' },
  'digital-asset': { label: 'Digital Asset', labelKo: '디지털 자산' },
  'debt-arbitrage': { label: 'Debt Arbitrage', labelKo: '부채 차익' },
  'solo-structure': { label: 'Solo Structure', labelKo: '1인 사업 구조' },
  'service-business': { label: 'Service Business', labelKo: '서비스업' },
  'diaspora-succession': { label: 'Diaspora Succession', labelKo: '교포 사업 승계' },
};

export const SOURCE_LABELS: Record<SourceType, string> = {
  'operator-interview': 'Operator verified',
  'public-filing': 'Public filing',
  'listing-data': 'Listing data',
  composite: 'Composite',
  illustrative: 'Illustrative',
};

export const EFFORT_LABELS: Record<EffortLevel, string> = {
  passive: 'Passive',
  'part-time': 'Part-time',
  'full-time': 'Full-time',
};

export const DIFFICULTY_LABELS: Record<DifficultyLevel, string> = {
  starter: 'Starter',
  intermediate: 'Intermediate',
  advanced: 'Advanced',
};

/* -------------------------------------------------------------------------- */
/*                                Case studies                                 */
/* -------------------------------------------------------------------------- */

export type PlaybookStep = {
  title: string;
  detail: string;
  /** e.g. "Weeks 1–2" */
  durationLabel?: string;
};

/** A cited primary source, matching the editorial `Source` shape used by guides. */
export type CaseStudySource = {
  label: string;
  url: string;
  publisher: string;
};

export interface CaseStudy {
  id: string;
  slug: string;

  title: string;
  /** One-line hook: "He bought a 4-machine laundromat with a seller note." */
  dek: string;
  category: CaseStudyCategory;
  /** Where the operator is, if it matters to the economics. */
  market: string | null;

  /* ------------------------------- Economics ------------------------------ */
  /** All-in capital to get to first dollar, in cents. */
  startupCostCents: number;
  /** Cash actually put in by the operator (rest is debt/seller financing). */
  cashInvestedCents: number | null;
  monthlyRevenueCents: number;
  monthlyExpenseCents: number;
  /** Owner hours per week — the honest denominator on "passive income". */
  hoursPerWeek: number | null;
  /** Months from first dollar spent to break-even. */
  monthsToBreakEven: number | null;

  effort: EffortLevel;
  difficulty: DifficultyLevel;

  /* -------------------------------- Content ------------------------------- */
  /** Long-form teardown body. Blank-line-separated paragraphs. */
  body: string;
  playbookSteps: PlaybookStep[];
  /** Tools/services the operator actually runs on. */
  techStack: string[];
  /** The parts that genuinely go wrong. Non-negotiable on every teardown. */
  risks: string[];
  tags: string[];

  /* ------------------------------ Provenance ------------------------------ */
  sourceType: SourceType;
  sourceNote: string;
  /** ISO date (YYYY-MM-DD). Last time the economics were re-checked. */
  verifiedAt: string;
  /**
   * Primary sources, where the piece leans on published rules rather than
   * modelled economics. The diaspora studies carry these because they make
   * claims about tax treaties and visa status that a reader can check.
   */
  sources?: CaseStudySource[];

  /* ------------------------------ Publishing ------------------------------ */
  featured: boolean;
  /** ISO date (YYYY-MM-DD). */
  publishedAt: string;
  readingMinutes: number;
  /** Tailwind gradient classes for the card cover. */
  gradient: string;
  /**
   * Why this sits on a Korean diaspora site. Rendered under the dek on studies
   * written for this audience; omitted on the ported ones, which the section
   * intro covers instead.
   */
  diasporaAngle?: string;
}

/* -------------------------------------------------------------------------- */
/*                                  Playbooks                                  */
/* -------------------------------------------------------------------------- */

export interface Playbook {
  slug: string;
  title: string;
  summary: string;
  /** Shown as the eyebrow on cards. */
  format: PlaybookFormat;
  pages: number;
  /** Case studies this playbook operationalises. */
  relatedCaseStudySlugs: string[];
  /**
   * Path to the downloadable asset, once it exists.
   *
   * Null on every playbook today, and the UI says so plainly rather than
   * linking at a file that would 404. Realiform gated these by paid tier; the
   * tiers are gone, so the only honest state left is "written, not yet
   * produced".
   */
  assetPath: string | null;
  includes: string[];
}
